import React, { useState, useEffect } from 'react'
import {
  Typography, Card, Table, Tag, Button, Modal, Form, Input, Select, Space,
  message, Tooltip, Statistic, Row, Col, Drawer, Descriptions, Badge, Divider, Alert, InputNumber
} from 'antd'
import {
  ApiOutlined, PlusOutlined, KeyOutlined, BarChartOutlined, EyeOutlined,
  CopyOutlined, DeleteOutlined, ReloadOutlined, DollarOutlined, RiseOutlined,
  GiftOutlined, StopOutlined, EditOutlined, UserOutlined
} from '@ant-design/icons'
import api from '../services/api'

const { Title, Text, Paragraph } = Typography
const { Option } = Select

const APISubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [keyModalVisible, setKeyModalVisible] = useState(false)
  const [usageDrawerVisible, setUsageDrawerVisible] = useState(false)
  const [complimentaryModalVisible, setComplimentaryModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [generatedKey, setGeneratedKey] = useState(null)
  const [usageData, setUsageData] = useState(null)
  const [complimentaryLoading, setComplimentaryLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [availableTiers, setAvailableTiers] = useState([])
  const [form] = Form.useForm()
  const [keyForm] = Form.useForm()
  const [complimentaryForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const tiers = [
    { value: 'developer', label: 'Developer ($99/mo - 100 req/day)' },
    { value: 'business', label: 'Business ($299/mo - 1,000 req/day)' },
    { value: 'enterprise', label: 'Enterprise ($999/mo - 10,000 req/day)' }
  ]

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'trialing', label: 'Trial', color: 'processing' },
    { value: 'suspended', label: 'Suspended', color: 'warning' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' },
    { value: 'complimentary', label: 'Complimentary', color: 'purple' }
  ]

  useEffect(() => {
    fetchSubscriptions()
    fetchAvailableTiers()
  }, [])

  const fetchAvailableTiers = async () => {
    try {
      const data = await api.get('/api-subscriptions/tiers')
      setAvailableTiers(data || [])
    } catch (error) {
      console.error('Failed to fetch tiers:', error)
    }
  }

  const fetchSubscriptions = async () => {
    setLoading(true)
    try {
      const data = await api.get('/api-subscriptions/subscriptions')
      setSubscriptions(data)
    } catch (error) {
      console.error('Failed to fetch API subscriptions:', error)
      message.error('Failed to load API subscriptions')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSubscription = async (values) => {
    try {
      await api.post('/api-subscriptions/subscriptions', values)
      message.success('API subscription created successfully')
      setCreateModalVisible(false)
      form.resetFields()
      fetchSubscriptions()
    } catch (error) {
      console.error('Failed to create subscription:', error)
      message.error(error?.detail || 'Failed to create subscription')
    }
  }

  const handleGenerateKey = async (values) => {
    try {
      const data = await api.post('/api-subscriptions/keys', {
        subscription_id: selectedSubscription.subscription_id,
        ...values
      })

      setGeneratedKey(data)
      message.success('API key generated successfully')
      keyForm.resetFields()
    } catch (error) {
      console.error('Failed to generate API key:', error)
      message.error(error?.detail || 'Failed to generate API key')
    }
  }

  const fetchUsageStats = async (subscription) => {
    try {
      const data = await api.get(
        `/api-subscriptions/subscriptions/${subscription.subscription_id}/usage?days=30`
      )
      setUsageData(data)
      setSelectedSubscription(subscription)
      setUsageDrawerVisible(true)
    } catch (error) {
      console.error('Failed to fetch usage stats:', error)
      message.error('Failed to load usage statistics')
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    message.success('Copied to clipboard')
  }

  const handleSetComplimentary = async (values) => {
    setComplimentaryLoading(true)
    try {
      const isCurrentlyComplimentary = selectedSubscription?.status === 'complimentary'
      const data = await api.post(
        `/api-subscriptions/subscriptions/${selectedSubscription.subscription_id}/complimentary`,
        {
          is_complimentary: !isCurrentlyComplimentary,
          reason: values.reason || null
        }
      )
      message.success(data.message)
      if (data.stripe_action) {
        message.info(`Stripe: ${data.stripe_action}`)
      }
      setComplimentaryModalVisible(false)
      complimentaryForm.resetFields()
      fetchSubscriptions()
    } catch (error) {
      console.error('Failed to update complimentary status:', error)
      message.error(error?.detail || 'Failed to update complimentary status')
    } finally {
      setComplimentaryLoading(false)
    }
  }

  const handleEditSubscription = async (values) => {
    setEditLoading(true)
    try {
      await api.put(
        `/api-subscriptions/subscriptions/${selectedSubscription.subscription_id}`,
        {
          tier_id: values.tier_id,
          status: values.status
        }
      )
      message.success('Subscription updated successfully')
      setEditModalVisible(false)
      editForm.resetFields()
      fetchSubscriptions()
    } catch (error) {
      console.error('Failed to update subscription:', error)
      message.error(error?.detail || 'Failed to update subscription')
    } finally {
      setEditLoading(false)
    }
  }

  const columns = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text, record) => (
        <div>
          <div><strong>{text}</strong></div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.contact_email}</Text>
        </div>
      )
    },
    {
      title: 'Tier',
      dataIndex: 'tier_display_name',
      key: 'tier',
      render: (text, record) => (
        <div>
          <Tag color="blue">{text}</Tag>
          <div style={{ fontSize: '12px', marginTop: 4 }}>
            <DollarOutlined /> {record.monthly_price}/mo
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const statusConfig = {
          active: { color: 'success', text: 'Active' },
          trial: { color: 'processing', text: 'Trial' },
          trialing: { color: 'processing', text: 'Trial' },
          suspended: { color: 'warning', text: 'Suspended' },
          cancelled: { color: 'error', text: 'Cancelled' },
          complimentary: { color: 'purple', text: 'Complimentary' }
        }
        const config = statusConfig[status] || { color: 'default', text: status }

        return (
          <div>
            <Tag color={config.color} icon={status === 'complimentary' ? <GiftOutlined /> : null}>
              {config.text}
            </Tag>
            {(status === 'trial' || status === 'trialing') && record.trial_ends_at && (
              <div style={{ fontSize: '11px', marginTop: 4 }}>
                Ends: {new Date(record.trial_ends_at).toLocaleDateString()}
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'Usage Today',
      key: 'usage',
      render: (_, record) => (
        <div>
          <div>
            <RiseOutlined /> {record.requests_today} / {record.daily_rate_limit}
          </div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
            {record.active_keys_count} active key{record.active_keys_count !== 1 ? 's' : ''}
          </div>
        </div>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Subscription">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setSelectedSubscription(record)
                editForm.setFieldsValue({
                  tier_id: record.tier_id,
                  status: record.status
                })
                setEditModalVisible(true)
              }}
            />
          </Tooltip>
          <Tooltip title="Generate API Key">
            <Button
              icon={<KeyOutlined />}
              size="small"
              onClick={() => {
                setSelectedSubscription(record)
                setKeyModalVisible(true)
              }}
            />
          </Tooltip>
          <Tooltip title="View Usage">
            <Button
              icon={<BarChartOutlined />}
              size="small"
              onClick={() => fetchUsageStats(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 'complimentary' ? 'Remove Complimentary' : 'Make Complimentary'}>
            <Button
              icon={record.status === 'complimentary' ? <StopOutlined /> : <GiftOutlined />}
              size="small"
              type={record.status === 'complimentary' ? 'default' : 'default'}
              style={record.status === 'complimentary' ? { color: '#722ed1' } : {}}
              onClick={() => {
                setSelectedSubscription(record)
                setComplimentaryModalVisible(true)
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title level={2} className="mb-0">
          <UserOutlined className="mr-2" />
          Subscriptions
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchSubscriptions}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            New Subscription
          </Button>
        </Space>
      </div>

      {/* Summary Stats */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Subscriptions"
              value={subscriptions.length}
              prefix={<ApiOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Subscriptions"
              value={subscriptions.filter(s => s.status === 'active').length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Trial Subscriptions"
              value={subscriptions.filter(s => s.status === 'trial').length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Complimentary"
              value={subscriptions.filter(s => s.status === 'complimentary').length}
              valueStyle={{ color: '#722ed1' }}
              prefix={<GiftOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Subscriptions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={subscriptions}
          rowKey="subscription_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create Subscription Modal */}
      <Modal
        title="Create API Subscription"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubscription}
        >
          <Form.Item
            name="company_name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Acme Corp" />
          </Form.Item>

          <Form.Item
            name="contact_email"
            label="Contact Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="dev@acme.com" />
          </Form.Item>

          <Form.Item
            name="tier"
            label="Subscription Tier"
            rules={[{ required: true, message: 'Please select a tier' }]}
          >
            <Select placeholder="Select tier">
              {tiers.map(tier => (
                <Option key={tier.value} value={tier.value}>{tier.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="trial_days"
            label="Trial Period (Optional)"
            help="Number of days for trial period"
          >
            <InputNumber
              min={0}
              max={90}
              placeholder="14"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => {
                setCreateModalVisible(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Subscription
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Generate API Key Modal */}
      <Modal
        title={`Generate API Key - ${selectedSubscription?.company_name}`}
        open={keyModalVisible}
        onCancel={() => {
          setKeyModalVisible(false)
          setGeneratedKey(null)
          keyForm.resetFields()
        }}
        footer={null}
        width={700}
      >
        {!generatedKey ? (
          <Form
            form={keyForm}
            layout="vertical"
            onFinish={handleGenerateKey}
          >
            <Form.Item
              name="key_name"
              label="Key Name"
              rules={[{ required: true, message: 'Please enter a key name' }]}
              help="Friendly name to identify this key (e.g., 'Production Server', 'Staging Environment')"
            >
              <Input placeholder="Production Key" />
            </Form.Item>

            <Form.Item
              name="expires_in_days"
              label="Expiration (Optional)"
              help="Number of days until key expires. Leave empty for no expiration."
            >
              <InputNumber
                min={1}
                max={3650}
                placeholder="365"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Space className="w-full justify-end">
                <Button onClick={() => {
                  setKeyModalVisible(false)
                  keyForm.resetFields()
                }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" icon={<KeyOutlined />}>
                  Generate Key
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <div>
            <Alert
              message="API Key Generated Successfully"
              description="This is the only time you'll see the full API key. Please save it securely."
              type="success"
              showIcon
              className="mb-4"
            />

            <Descriptions bordered column={1}>
              <Descriptions.Item label="Key Name">
                {generatedKey.key_name}
              </Descriptions.Item>
              <Descriptions.Item label="API Key">
                <div className="flex items-center gap-2">
                  <Text code copyable style={{ flex: 1, wordBreak: 'break-all' }}>
                    {generatedKey.api_key}
                  </Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Key Prefix (for display)">
                <Text type="secondary">{generatedKey.api_key_prefix}...</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Expires">
                {generatedKey.expires_at
                  ? new Date(generatedKey.expires_at).toLocaleString()
                  : 'Never'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(generatedKey.created_at).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div className="bg-gray-50 p-4 rounded">
              <Text strong>Example Usage:</Text>
              <pre className="mt-2 bg-gray-900 text-green-400 p-3 rounded overflow-x-auto text-xs">
{`curl -X POST https://api.acis-trading.com/v1/portfolios/generate \\
  -H "X-API-Key: ${generatedKey.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "strategy": "growth",
    "market_cap": "large",
    "investment_amount": 100000
  }'`}
              </pre>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                type="primary"
                onClick={() => {
                  setKeyModalVisible(false)
                  setGeneratedKey(null)
                  keyForm.resetFields()
                  fetchSubscriptions()
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Usage Drawer */}
      <Drawer
        title={`Usage Statistics - ${selectedSubscription?.company_name}`}
        placement="right"
        width={600}
        onClose={() => setUsageDrawerVisible(false)}
        open={usageDrawerVisible}
      >
        {usageData && (
          <div>
            <Row gutter={16} className="mb-6">
              <Col span={12}>
                <Card>
                  <Statistic
                    title="Total Requests (30 days)"
                    value={usageData.total_requests}
                    prefix={<RiseOutlined />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="Success Rate"
                    value={
                      usageData.total_requests > 0
                        ? ((usageData.successful_requests / usageData.total_requests) * 100).toFixed(1)
                        : 0
                    }
                    suffix="%"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="Performance" className="mb-4">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Avg Response Time">
                  {usageData.avg_response_time_ms.toFixed(0)} ms
                </Descriptions.Item>
                <Descriptions.Item label="Successful Requests">
                  {usageData.successful_requests.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Failed Requests">
                  <Text type={usageData.failed_requests > 0 ? 'danger' : 'secondary'}>
                    {usageData.failed_requests.toLocaleString()}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Endpoints Used" className="mb-4">
              <div className="space-y-2">
                {Object.entries(usageData.endpoints_used || {}).map(([endpoint, count]) => (
                  <div key={endpoint} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <Text code className="text-xs">{endpoint}</Text>
                    <Badge count={count} style={{ backgroundColor: '#1890ff' }} />
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Daily Breakdown (Last 7 Days)">
              <Table
                size="small"
                dataSource={usageData.daily_breakdown?.slice(0, 7) || []}
                rowKey="date"
                pagination={false}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date'
                  },
                  {
                    title: 'Requests',
                    dataIndex: 'requests',
                    key: 'requests',
                    render: (val) => val.toLocaleString()
                  },
                  {
                    title: 'Success',
                    dataIndex: 'successes',
                    key: 'successes',
                    render: (val) => <Text type="success">{val}</Text>
                  },
                  {
                    title: 'Errors',
                    dataIndex: 'errors',
                    key: 'errors',
                    render: (val) => val > 0 ? <Text type="danger">{val}</Text> : val
                  }
                ]}
              />
            </Card>
          </div>
        )}
      </Drawer>

      {/* Complimentary Status Modal */}
      <Modal
        title={
          selectedSubscription?.status === 'complimentary'
            ? `Remove Complimentary - ${selectedSubscription?.company_name}`
            : `Make Complimentary - ${selectedSubscription?.company_name}`
        }
        open={complimentaryModalVisible}
        onCancel={() => {
          setComplimentaryModalVisible(false)
          complimentaryForm.resetFields()
        }}
        footer={null}
        width={500}
      >
        {selectedSubscription?.status === 'complimentary' ? (
          <div>
            <Alert
              message="Remove Complimentary Access"
              description="This will change the account status back to 'Active'. You may need to manually set up billing in Stripe if you want to charge this customer."
              type="warning"
              showIcon
              className="mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setComplimentaryModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                icon={<StopOutlined />}
                loading={complimentaryLoading}
                onClick={() => handleSetComplimentary({})}
              >
                Remove Complimentary
              </Button>
            </div>
          </div>
        ) : (
          <Form
            form={complimentaryForm}
            layout="vertical"
            onFinish={handleSetComplimentary}
          >
            <Alert
              message="Grant Complimentary Access"
              description={
                <div>
                  <p>This will:</p>
                  <ul className="list-disc ml-4 mt-2">
                    <li>Cancel their Stripe subscription (no more billing)</li>
                    <li>Grant unlimited free access forever</li>
                    <li>Change status to "Complimentary"</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <Form.Item
              name="reason"
              label="Reason for Complimentary Access"
              help="Optional - helps track why this account is free"
            >
              <Select placeholder="Select a reason" allowClear>
                <Option value="friend_family">Friend / Family</Option>
                <Option value="beta_tester">Beta Tester</Option>
                <Option value="partner">Partner / Affiliate</Option>
                <Option value="internal">Internal / Testing</Option>
                <Option value="demo">Demo Account</Option>
                <Option value="promotion">Promotional</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item className="mb-0">
              <Space className="w-full justify-end">
                <Button onClick={() => {
                  setComplimentaryModalVisible(false)
                  complimentaryForm.resetFields()
                }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<GiftOutlined />}
                  loading={complimentaryLoading}
                  style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                >
                  Make Complimentary
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Edit Subscription Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <EditOutlined />
            <span>Edit Subscription - {selectedSubscription?.company_name}</span>
          </div>
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          editForm.resetFields()
        }}
        footer={null}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubscription}
        >
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <Text type="secondary">
              <strong>Email:</strong> {selectedSubscription?.contact_email}
            </Text>
          </div>

          <Form.Item
            name="tier_id"
            label="Subscription Tier"
            rules={[{ required: true, message: 'Please select a tier' }]}
          >
            <Select placeholder="Select tier">
              {availableTiers.map(tier => (
                <Option key={tier.tier_id} value={tier.tier_id}>
                  {tier.tier_display_name} - ${tier.monthly_price}/mo ({tier.daily_rate_limit.toLocaleString()} req/day)
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Subscription Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status">
              {statusOptions.map(status => (
                <Option key={status.value} value={status.value}>
                  <Tag color={status.color} style={{ marginRight: 8 }}>{status.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Alert
            message="Status Changes"
            description={
              <ul className="list-disc ml-4 text-xs">
                <li><strong>Active:</strong> Full access, normal billing</li>
                <li><strong>Trial:</strong> Trial period, limited time</li>
                <li><strong>Suspended:</strong> Access blocked, billing paused</li>
                <li><strong>Cancelled:</strong> No access, subscription ended</li>
                <li><strong>Complimentary:</strong> Free access (use dedicated button for this)</li>
              </ul>
            }
            type="info"
            showIcon
            className="mb-4"
          />

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => {
                setEditModalVisible(false)
                editForm.resetFields()
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<EditOutlined />}
                loading={editLoading}
              >
                Update Subscription
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default APISubscriptions
